// pages/ProfilePage.jsx
// Page de profil utilisateur

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LONGUEUR_MIN_MOT_DE_PASSE } from '../config/motDePasse';
import { User, Shield, Key, Save, Globe, Languages } from 'lucide-react';
import LangueInterfaceSelector from '../components/LangueInterfaceSelector';
import { getLangueFlag } from '../utils/exerciseUtils';
import './ProfilePage.css';
import ChampMotDePasse from '../components/ChampMotDePasse';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const NIVEAU_LABELS = {
    gratuit: t('profile.niveauGratuit'),
    testeur: t('profile.niveauTesteur'),
    basique: t('profile.niveauBasique'),
    premium: t('profile.niveauPremium'),
    admin: t('profile.niveauAdmin'),
  };

  // État édition profil
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [profilMsg, setProfilMsg] = useState('');
  const [profilErr, setProfilErr] = useState('');
  const [profilSaving, setProfilSaving] = useState(false);

  // État changement mot de passe
  const [ancienMdp, setAncienMdp] = useState('');
  const [nouveauMdp, setNouveauMdp] = useState('');
  const [confirmMdp, setConfirmMdp] = useState('');
  const [mdpMsg, setMdpMsg] = useState('');
  const [mdpErr, setMdpErr] = useState('');
  const [mdpSaving, setMdpSaving] = useState(false);

  // Niveaux par langue
  const [niveaux, setNiveaux] = useState([]);

  useEffect(() => {
    if (user) {
      setNom(user.nom || '');
      setPrenom(user.prenom || '');
      fetchNiveaux();
    }
  }, [user]);

  const fetchNiveaux = async () => {
    try {
      const res = await fetch(`/api/users/${user.id}/niveaux`);
      if (res.ok) {
        const data = await res.json();
        setNiveaux(data.niveaux || []);
      }
    } catch (err) {
      // Silencieux - les niveaux ne sont pas critiques
    }
  };

  const handleProfilSubmit = async (e) => {
    e.preventDefault();
    setProfilMsg('');
    setProfilErr('');
    setProfilSaving(true);

    try {
      const res = await fetch('/api/auth/profil', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom, prenom }),
      });

      if (!res.ok) {
        throw new Error(t('profile.erreurMiseAJour'));
      }

      setProfilMsg(t('profile.profilMisAJour'));
    } catch (err) {
      setProfilErr(err.message);
    } finally {
      setProfilSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMdpMsg('');
    setMdpErr('');

    if (nouveauMdp !== confirmMdp) {
      setMdpErr(t('profile.mdpNeCorrespondentPas'));
      return;
    }

    if (Array.from(nouveauMdp).length < LONGUEUR_MIN_MOT_DE_PASSE) {
      setMdpErr(t('profile.mdpMinLength', { min: LONGUEUR_MIN_MOT_DE_PASSE }));
      return;
    }

    setMdpSaving(true);

    try {
      const res = await fetch('/api/auth/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ancienMotDePasse: ancienMdp,
          nouveauMotDePasse: nouveauMdp,
        }),
      });

      if (!res.ok) {
        throw new Error(res.status === 401 ? t('profile.mdpActuelIncorrect') : t('profile.erreurChangementMdp'));
      }

      setMdpMsg(t('profile.motDePasseModifie'));
      setAncienMdp('');
      setNouveauMdp('');
      setConfirmMdp('');
    } catch (err) {
      setMdpErr(err.message);
    } finally {
      setMdpSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="profile-page">
      <h1>
        <User size={28} /> {t('profile.title')}
      </h1>

      {/* Infos compte */}
      <div className="profile-card">
        <h2>{t('profile.infosCompte')}</h2>
        <div className="profile-info-grid">
          <div className="profile-info-item">
            <span className="profile-label">{t('profile.email')}</span>
            <span className="profile-value">{user.email}</span>
          </div>
          <div className="profile-info-item">
            <span className="profile-label">{t('profile.abonnement')}</span>
            <span className={`profile-badge badge-${user.niveauAcces}`}>{NIVEAU_LABELS[user.niveauAcces] || user.niveauAcces}</span>
          </div>
          {/* {user.niveauAcces === 'admin' && (
            <div className="profile-info-item">
              <span className="profile-label">Rôle</span>
              <span className="profile-badge badge-admin">
                <Shield size={14} /> Administrateur
              </span>
            </div>
          )} */}
        </div>
      </div>

      {/* Langue de l'interface */}
      <div className="profile-card">
        <h2>
          <Languages size={20} /> {t('profile.langueInterface')}
        </h2>
        <p className="profile-langue-hint">{t('profile.langueInterfaceHint')}</p>
        <LangueInterfaceSelector className="profile-langue-select" />
      </div>

      {/* Niveaux par langue */}
      {niveaux.length > 0 && (
        <div className="profile-card">
          <h2>
            <Globe size={20} /> {t('profile.niveauxParLangue')}
          </h2>
          <div className="profile-niveaux">
            {niveaux.map((n) => (
              <div key={n.langue} className="profile-niveau-item">
                <span className="profile-niveau-flag">{getLangueFlag(n.langue)}</span>
                <span className="profile-niveau-lang">{t(`langues.${n.langue}`, { defaultValue: n.langue })}</span>
                <span className="profile-niveau-value">{t('profile.niveauLabel', { niveau: n.niveau })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Édition profil */}
      <div className="profile-card">
        <h2>{t('profile.modifierProfil')}</h2>

        {profilMsg && <div className="profile-success">{profilMsg}</div>}
        {profilErr && <div className="profile-error">{profilErr}</div>}

        <form onSubmit={handleProfilSubmit} className="profile-form">
          <div className="profile-form-row">
            <div className="form-group">
              <label htmlFor="prenom">{t('profile.prenom')}</label>
              <input id="prenom" type="text" value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder={t('profile.prenomPlaceholder')} />
            </div>
            <div className="form-group">
              <label htmlFor="nom">{t('profile.nom')}</label>
              <input id="nom" type="text" value={nom} onChange={(e) => setNom(e.target.value)} placeholder={t('profile.nomPlaceholder')} />
            </div>
          </div>
          <button type="submit" className="profile-btn" disabled={profilSaving}>
            <Save size={16} />
            {profilSaving ? t('profile.enregistrement') : t('profile.enregistrer')}
          </button>
        </form>
      </div>

      {/* Changement mot de passe */}
      <div className="profile-card">
        <h2>
          <Key size={20} /> {t('profile.changerMotDePasse')}
        </h2>

        {mdpMsg && <div className="profile-success">{mdpMsg}</div>}
        {mdpErr && <div className="profile-error">{mdpErr}</div>}

        <form onSubmit={handlePasswordSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="ancien-mdp">{t('profile.motDePasseActuel')}</label>
            <ChampMotDePasse
              id="ancien-mdp"
              value={ancienMdp}
              onChange={(e) => setAncienMdp(e.target.value)}
              placeholder={t('profile.motDePasseActuelPlaceholder')}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="nouveau-mdp">{t('profile.nouveauMotDePasse')}</label>
            <ChampMotDePasse
              id="nouveau-mdp"
              value={nouveauMdp}
              onChange={(e) => setNouveauMdp(e.target.value)}
              placeholder={t('profile.nouveauMotDePassePlaceholder', { min: LONGUEUR_MIN_MOT_DE_PASSE })}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirm-mdp">{t('profile.confirmerMotDePasse')}</label>
            <ChampMotDePasse
              id="confirm-mdp"
              value={confirmMdp}
              onChange={(e) => setConfirmMdp(e.target.value)}
              placeholder={t('profile.confirmerMotDePassePlaceholder')}
              required
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="profile-btn" disabled={mdpSaving}>
            <Key size={16} />
            {mdpSaving ? t('profile.modification') : t('profile.modifierMotDePasseBtn')}
          </button>
        </form>
      </div>
    </div>
  );
}
